/** Server-authoritative progression: profile data shape and all rules that
 * mutate it (XP, quests, battle pass, rating, ownership). Pure functions that
 * mutate a ProfileData in place and return user-facing notices; persistence
 * and messaging live in the server. Numbers are 1:1 from the prototype. */
import {
  CARDBACKS, EXTRA_EMOTES, FELTS, FRAMES, PASS, PASS_TIERS, PLACE_DELTA, QUEST_POOL,
  SEASON_REWARD_TALER, TITLES, WEEKLY_QUEST_POOL, XP_PER_TIER, currentSeason, rankFor, rewardName,
  type QuestDef, type Reward, type Season,
} from './data.js';
import { CAT_NAMES, catOf } from './cards.js';
import { HEROES } from './heroes.js';

export interface ProfileStats {
  hands: number;
  handsWon: number;
  showdownsWon: number;
  tournaments: number;
  wins: number;
  top3: number;
  bestHand: string;
  bestHandScore: number;
  biggestPot: number;
  emotes: number;
}

export interface QuestStateData {
  id: string;
  progress: number;
  done: boolean;
}

export interface ProfileData {
  name: string;
  xp: number;
  passXP: number;
  premium: boolean;
  taler: number;
  rating: number;
  peakRating: number;
  hero: string;
  skins: Record<string, string>;
  cardback: string;
  felt: string;
  frame: string;
  title: string;
  owned: { cardback: string[]; felt: string[]; frame: string[]; title: string[]; emote: string[]; skin: string[] };
  claimedFree: number[];
  claimedPremium: number[];
  stats: ProfileStats;
  quests: { date: string; list: QuestStateData[] };
  /** weekly quests, keyed by ISO week (e.g. "2026-W36"); additive field so
   * profiles from before the feature keep working (db merges over defaults) */
  weeklyQuests: { week: string; list: QuestStateData[] };
  /** number of the season this profile's rating belongs to */
  season: number;
}

export interface Notice {
  kind: 'xp' | 'levelup' | 'quest' | 'reward' | 'info';
  title: string;
  sub?: string;
}

export const levelOf = (xp: number): number => Math.floor(xp / XP_PER_TIER) + 1;
export const passTierOf = (passXP: number): number => Math.min(PASS_TIERS, Math.floor(passXP / XP_PER_TIER));

export function defaultProfileData(name: string, seasonNum: number = currentSeason().num): ProfileData {
  return {
    name,
    xp: 0,
    passXP: 0,
    premium: false,
    taler: 120,
    rating: 1000,
    peakRating: 1000,
    hero: 'brunhild',
    skins: {},
    cardback: 'tavern',
    felt: 'green',
    frame: 'wood',
    title: 'neuling',
    owned: {
      cardback: ['tavern'], felt: ['green'], frame: ['wood'], title: ['neuling'], emote: [],
      skin: HEROES.map((h) => h.id + ':base'),
    },
    claimedFree: [],
    claimedPremium: [],
    stats: { hands: 0, handsWon: 0, showdownsWon: 0, tournaments: 0, wins: 0, top3: 0, bestHand: '–', bestHandScore: 0, biggestPot: 0, emotes: 0 },
    quests: { date: '', list: [] },
    weeklyQuests: { week: '', list: [] },
    season: seasonNum,
  };
}

/** Season rollover: pays the season-end reward for the final rank and applies
 * a soft rating reset (halfway back to 1000). Idempotent per season; a profile
 * that skipped whole seasons is settled once, for its stored final rank. */
export function ensureSeason(p: ProfileData, target: Season): { changed: boolean; notices: Notice[] } {
  if (p.season >= target.num) return { changed: false, notices: [] };
  const rk = rankFor(p.rating);
  const taler = SEASON_REWARD_TALER[rk.id] ?? 0;
  p.taler += taler;
  const oldRating = p.rating;
  p.rating = Math.round((p.rating + 1000) / 2);
  p.season = target.num;
  const notices: Notice[] = [
    { kind: 'reward', title: 'Saison beendet – Rang ' + rk.name, sub: '+' + taler + ' Taler Saisonbelohnung' },
    { kind: 'info', title: 'Saison ' + target.num + ': ' + target.name, sub: 'Wertung ' + oldRating + ' → ' + p.rating + ' (weicher Reset)' },
  ];
  return { changed: true, notices };
}

export function ownsItem(p: ProfileData, type: keyof ProfileData['owned'], id: string): boolean {
  return (p.owned[type] ?? []).includes(id);
}

export function ownedHeroIdsOf(p: ProfileData): string[] {
  return HEROES.filter((h) => p.owned.skin.includes(h.id + ':base')).map((h) => h.id);
}

export function grantReward(p: ProfileData, r: Reward, silent = false): Notice[] {
  if (r.type === 'taler') p.taler += r.amount;
  else if (r.type === 'skin') {
    const k = r.hero + ':' + r.id;
    if (!p.owned.skin.includes(k)) p.owned.skin.push(k);
  } else if (!p.owned[r.type].includes(r.id)) {
    p.owned[r.type].push(r.id);
  }
  return silent ? [] : [{ kind: 'reward', title: 'Belohnung erhalten', sub: rewardName(r) }];
}

export function claimPassRewards(p: ProfileData): Notice[] {
  const notices: Notice[] = [];
  const t = passTierOf(p.passXP);
  for (const row of PASS) {
    if (row.tier > t) break;
    if (!p.claimedFree.includes(row.tier)) {
      p.claimedFree.push(row.tier);
      notices.push(...grantReward(p, row.free));
    }
    if (p.premium && !p.claimedPremium.includes(row.tier)) {
      p.claimedPremium.push(row.tier);
      notices.push(...grantReward(p, row.premium));
    }
  }
  return notices;
}

export function addXp(p: ProfileData, n: number, why?: string): Notice[] {
  if (n <= 0) return [];
  const notices: Notice[] = [];
  const before = levelOf(p.xp);
  p.xp += n;
  p.passXP += n;
  if (why) notices.push({ kind: 'xp', title: '+' + n + ' EP', sub: why });
  if (levelOf(p.xp) > before) {
    notices.push({ kind: 'levelup', title: 'Stufe ' + levelOf(p.xp) + ' erreicht!', sub: 'Dein Ruf in der Taverne wächst.' });
  }
  notices.push(...claimPassRewards(p));
  return notices;
}

function rollQuests(pool: QuestDef[], count: number, rand: () => number): QuestStateData[] {
  const rest = [...pool];
  const list: QuestStateData[] = [];
  while (list.length < count && rest.length) {
    const i = Math.floor(rand() * rest.length);
    const q = rest.splice(i, 1)[0]!;
    list.push({ id: q.id, progress: 0, done: false });
  }
  return list;
}

/** Rolls three daily quests if the stored date differs. Returns true if changed. */
export function ensureDailyQuests(p: ProfileData, dateKey: string, rand: () => number = Math.random): boolean {
  if (p.quests.date === dateKey && p.quests.list.length) return false;
  p.quests = { date: dateKey, list: rollQuests(QUEST_POOL, 3, rand) };
  return true;
}

/** Rolls three weekly quests if the stored ISO week differs. Returns true if changed. */
export function ensureWeeklyQuests(p: ProfileData, weekKey: string, rand: () => number = Math.random): boolean {
  if (p.weeklyQuests.week === weekKey && p.weeklyQuests.list.length) return false;
  p.weeklyQuests = { week: weekKey, list: rollQuests(WEEKLY_QUEST_POOL, 3, rand) };
  return true;
}

function progressList(p: ProfileData, list: QuestStateData[], pool: QuestDef[], label: string, key: string, n: number): Notice[] {
  const notices: Notice[] = [];
  for (const q of list) {
    const def = pool.find((d) => d.id === q.id);
    if (!def || q.done || def.key !== key) continue;
    q.progress = Math.min(def.goal, q.progress + n);
    if (q.progress >= def.goal) {
      q.done = true;
      p.taler += def.taler;
      notices.push({ kind: 'quest', title: label + ' erfüllt: ' + def.text, sub: '+' + def.xp + ' EP · +' + def.taler + ' Taler' });
      notices.push(...addXp(p, def.xp));
    }
  }
  return notices;
}

export function applyQuestProgress(p: ProfileData, key: string, n = 1): Notice[] {
  return [
    ...progressList(p, p.quests.list, QUEST_POOL, 'Quest', key, n),
    ...progressList(p, p.weeklyQuests.list, WEEKLY_QUEST_POOL, 'Wochenquest', key, n),
  ];
}

export interface HandResultInfo {
  won: number;
  wentShowdown: boolean;
  wonShowdown: boolean;
  wasAllIn: boolean;
  stolen: boolean;
  score: number;
}

/** Stats, quests and XP for one finished hand. Returns the XP granted. */
export function applyHandResult(p: ProfileData, info: HandResultInfo): { xp: number; notices: Notice[] } {
  const notices: Notice[] = [];
  let xp = 10;
  let why = 'Hand gespielt';
  p.stats.hands++;
  notices.push(...applyQuestProgress(p, 'hands'));
  if (info.won > 0) {
    p.stats.handsWon++;
    if (info.won > p.stats.biggestPot) p.stats.biggestPot = info.won;
    notices.push(...applyQuestProgress(p, 'wins'));
    xp += 25;
    why = 'Hand gewonnen';
    if (info.wasAllIn) notices.push(...applyQuestProgress(p, 'allinwin'));
    if (info.stolen) notices.push(...applyQuestProgress(p, 'steals'));
    if (info.wonShowdown) {
      p.stats.showdownsWon++;
      if (info.score > p.stats.bestHandScore) {
        p.stats.bestHandScore = info.score;
        p.stats.bestHand = CAT_NAMES[catOf(info.score)] ?? '–';
      }
      if (catOf(info.score) >= 5) {
        xp += 50;
        why = 'Gewonnen mit ' + (CAT_NAMES[catOf(info.score)] ?? '');
        notices.push(...applyQuestProgress(p, 'bighand'));
      }
    }
  }
  notices.push(...addXp(p, xp, why));
  return { xp, notices };
}

export interface TournamentOutcome {
  bonusXp: number;
  taler: number;
  ratingDelta: number;
  newRating: number;
}

/** Placement bonus, taler, rating (with rank protection) and rank rewards. */
export function applyTournamentResult(
  p: ProfileData,
  opts: { mode: 'ranked' | 'casual'; placement: number; tableSize: number },
): { outcome: TournamentOutcome; notices: Notice[] } {
  const notices: Notice[] = [];
  const { mode, placement, tableSize } = opts;
  p.stats.tournaments++;
  if (placement === 1) p.stats.wins++;
  if (placement <= 3) {
    p.stats.top3++;
    notices.push(...applyQuestProgress(p, 'top3'));
  }
  const bonusXp = ({ 1: 400, 2: 250, 3: 150 } as Record<number, number>)[placement] ?? 60;
  const taler = placement === 1 ? 60 : placement <= 3 ? 30 : 10;
  p.taler += taler;
  let delta = 0;
  if (mode === 'ranked') {
    delta = PLACE_DELTA[tableSize]?.[placement - 1] ?? 0;
    const rk = rankFor(p.rating);
    // rank protection: just above a threshold you cannot drop below it
    if (delta < 0 && p.rating < rk.min + 15 && rk.min > 0) delta = Math.max(delta, -(p.rating - rk.min));
    p.rating = Math.max(0, p.rating + delta);
    p.peakRating = Math.max(p.peakRating, p.rating);
  }
  notices.push(...addXp(p, bonusXp, 'Turnier beendet: Platz ' + placement));
  const rk = rankFor(p.rating);
  if ((rk.id === 'diamant' || rk.id === 'legende') && !ownsItem(p, 'cardback', 'gold')) {
    notices.push(...grantReward(p, { type: 'cardback', id: 'gold' }));
  }
  if (rk.id === 'legende') {
    if (!ownsItem(p, 'frame', 'dragon')) notices.push(...grantReward(p, { type: 'frame', id: 'dragon' }));
    if (!ownsItem(p, 'title', 'legende')) notices.push(...grantReward(p, { type: 'title', id: 'legende' }));
  }
  return { outcome: { bonusXp, taler, ratingDelta: delta, newRating: p.rating }, notices };
}

const EQUIP_CATALOG: Record<'cardback' | 'felt' | 'frame' | 'title', string[]> = {
  cardback: CARDBACKS.map((c) => c.id),
  felt: FELTS.map((c) => c.id),
  frame: FRAMES.map((c) => c.id),
  title: TITLES.map((c) => c.id),
};

export function equipItem(p: ProfileData, slot: 'cardback' | 'felt' | 'frame' | 'title', id: string): boolean {
  if (!EQUIP_CATALOG[slot].includes(id) || !ownsItem(p, slot, id)) return false;
  p[slot] = id;
  return true;
}

export function equipSkin(p: ProfileData, heroId: string, skinId: string): boolean {
  if (!ownsItem(p, 'skin', heroId + ':' + skinId)) return false;
  p.skins[heroId] = skinId;
  return true;
}

export function setFavoriteHero(p: ProfileData, heroId: string): boolean {
  if (!HEROES.some((h) => h.id === heroId)) return false;
  p.hero = heroId;
  return true;
}

export const PREMIUM_PRICE = 950;

export function buyPremiumPass(p: ProfileData): { ok: boolean; notices: Notice[] } {
  if (p.premium) return { ok: true, notices: [] };
  if (p.taler < PREMIUM_PRICE) {
    return { ok: false, notices: [{ kind: 'info', title: 'Nicht genug Taler', sub: 'Du hast ' + p.taler + '. Quests und Turniere bringen Taler.' }] };
  }
  p.taler -= PREMIUM_PRICE;
  p.premium = true;
  const notices: Notice[] = [{ kind: 'reward', title: 'Premium-Pass aktiviert', sub: 'Alle erreichten Premium-Stufen werden freigeschaltet.' }];
  notices.push(...claimPassRewards(p));
  return { ok: true, notices };
}

/** Emotes owned by the profile (for validating emote texts server-side). */
export function allowedEmoteTexts(p: ProfileData, heroId: string): string[] {
  const hero = HEROES.find((h) => h.id === heroId);
  const base = hero ? [...hero.emotes] : [];
  for (const e of EXTRA_EMOTES) if (ownsItem(p, 'emote', e.id)) base.push(e.text);
  return base;
}
